import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from './common/authFetch';
import { useAuth } from './common/AuthContext';
import { isAdmin } from './common/utils';
import './css/Board.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Board() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdminUser = isAdmin(user);

    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    // 상세 보기
    const [selectedPost, setSelectedPost] = useState(null);

    // 작성/수정 모드
    const [editMode, setEditMode] = useState(false); // 'create' | 'edit' | false
    const [editPost, setEditPost] = useState({ title: '', content: '', visibility: 'public', is_pinned: false });
    const [editPostId, setEditPostId] = useState(null);

    // 댓글
    const [replies, setReplies] = useState([]);
    const [replyContent, setReplyContent] = useState('');
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [editingReplyContent, setEditingReplyContent] = useState('');

    const fetchPosts = useCallback(async (p = 1) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/posts?category=suggestion&page=${p}&limit=20`);
            const data = await res.json();
            if (data.success) {
                setPosts(data.posts);
                setTotal(data.total);
                setPage(data.page);
                setTotalPages(data.totalPages);
            }
        } catch {
            // silent
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const fetchDetail = async (postId) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/posts/${postId}`);
            const data = await res.json();
            if (data.success) {
                setSelectedPost(data.post);
                fetchReplies(postId);
            }
        } catch {
            // silent
        }
    };

    const fetchReplies = async (postId) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/posts/${postId}/replies`);
            const data = await res.json();
            if (data.success) setReplies(data.replies || []);
        } catch { /* silent */ }
    };

    const handleReplySubmit = async () => {
        if (!replyContent.trim() || !selectedPost) return;
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/posts/${selectedPost.post_id}/replies`, {
                method: 'POST',
                body: JSON.stringify({ content: replyContent.trim() })
            });
            const data = await res.json();
            if (data.success) {
                setReplyContent('');
                fetchReplies(selectedPost.post_id);
            }
        } catch { /* silent */ }
    };

    const handleReplyEdit = async (replyId) => {
        if (!editingReplyContent.trim()) return;
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/replies/${replyId}`, {
                method: 'PUT',
                body: JSON.stringify({ content: editingReplyContent.trim() })
            });
            const data = await res.json();
            if (data.success) {
                setEditingReplyId(null);
                setEditingReplyContent('');
                fetchReplies(selectedPost.post_id);
            }
        } catch { /* silent */ }
    };

    const handleReplyDelete = async (replyId) => {
        if (!window.confirm(t('board.confirmDeleteReply'))) return;
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/replies/${replyId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) fetchReplies(selectedPost.post_id);
        } catch { /* silent */ }
    };

    const handleCreate = () => {
        setEditMode('create');
        setEditPost({ title: '', content: '', visibility: 'public', is_pinned: false });
        setEditPostId(null);
    };

    const handleEdit = () => {
        if (!selectedPost) return;
        setEditMode('edit');
        setEditPost({
            title: selectedPost.title,
            content: selectedPost.content || '',
            visibility: selectedPost.visibility,
            is_pinned: selectedPost.is_pinned || false
        });
        setEditPostId(selectedPost.post_id);
    };

    const handleSubmit = async () => {
        if (!editPost.title.trim()) return;
        try {
            const url = editMode === 'create'
                ? `${API_URL}/api/v1/board/posts`
                : `${API_URL}/api/v1/board/posts/${editPostId}`;
            const method = editMode === 'create' ? 'POST' : 'PUT';

            const res = await authFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...editPost,
                    category: 'suggestion'
                })
            });
            const data = await res.json();
            if (data.success) {
                setEditMode(false);
                setSelectedPost(null);
                fetchPosts(page);
            }
        } catch {
            // silent
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm(t('board.confirmDelete'))) return;
        try {
            const res = await authFetch(`${API_URL}/api/v1/board/posts/${postId}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                setSelectedPost(null);
                fetchPosts(page);
            }
        } catch {
            // silent
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}.${m}.${day} ${h}:${min}`;
    };

    const getDisplayName = (post) => {
        const u = post.user_info;
        if (!u) return '';
        return u.display_name || u.username;
    };

    // 작성/수정 폼
    if (editMode) {
        return (
            <div className="board-container">
                <div className="board-form">
                    <h2>{editMode === 'create' ? t('board.write') : t('board.edit')}</h2>
                    <input
                        className="board-form-title"
                        type="text"
                        placeholder={t('board.titlePlaceholder')}
                        value={editPost.title}
                        onChange={e => setEditPost({ ...editPost, title: e.target.value })}
                        maxLength={255}
                    />
                    <textarea
                        className="board-form-content"
                        placeholder={t('board.contentPlaceholder')}
                        value={editPost.content}
                        onChange={e => setEditPost({ ...editPost, content: e.target.value })}
                        rows={10}
                    />
                    <div className="board-form-visibility">
                        <label>{t('board.visibility')}:</label>
                        <select
                            value={editPost.visibility}
                            onChange={e => setEditPost({ ...editPost, visibility: e.target.value })}
                        >
                            <option value="public">{t('board.public')}</option>
                            <option value="private">{t('board.private')}</option>
                        </select>
                    </div>
                    {isAdminUser && (
                        <label className="board-form-pin">
                            <input
                                type="checkbox"
                                checked={editPost.is_pinned}
                                onChange={e => setEditPost({ ...editPost, is_pinned: e.target.checked })}
                            />
                            {t('board.pinToTop')}
                        </label>
                    )}
                    <div className="board-form-actions">
                        <button className="board-btn board-btn-cancel" onClick={() => setEditMode(false)}>
                            {t('cancel')}
                        </button>
                        <button className="board-btn board-btn-submit" onClick={handleSubmit} disabled={!editPost.title.trim()}>
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 상세 보기
    if (selectedPost) {
        const isMine = selectedPost.user_id === user?.user_id;
        return (
            <div className="board-container">
                <button className="board-back-btn" onClick={() => { setSelectedPost(null); fetchPosts(page); }}>
                    &#8592; {t('board.backToList')}
                </button>
                <article className="board-detail">
                    <div className="board-detail-header">
                        <h2 className="board-detail-title">
                            {selectedPost.is_pinned && (
                                <span className="board-pinned-badge">{t('board.pinned')}</span>
                            )}
                            {selectedPost.visibility === 'private' && (
                                <span className="board-private-badge">{t('board.private')}</span>
                            )}
                            {selectedPost.title}
                        </h2>
                        <div className="board-detail-meta">
                            <span>
                                {selectedPost.is_author_admin && <span className="board-admin-badge">{t('board.admin')}</span>}
                                {getDisplayName(selectedPost)}
                            </span>
                            <span>{formatDate(selectedPost.created_at)}</span>
                        </div>
                    </div>
                    <div className="board-detail-content">
                        {selectedPost.content?.split('\n').map((line, i) => (
                            <React.Fragment key={i}>{line}<br /></React.Fragment>
                        ))}
                    </div>
                    {(isMine || isAdminUser) && (
                        <div className="board-detail-actions">
                            {(isMine || isAdminUser) && (
                                <button className="board-btn board-btn-edit" onClick={handleEdit}>
                                    {t('board.edit')}
                                </button>
                            )}
                            <button className="board-btn board-btn-delete" onClick={() => handleDelete(selectedPost.post_id)}>
                                {t('board.delete')}
                            </button>
                        </div>
                    )}
                </article>

                {/* 댓글 섹션 */}
                <div className="board-replies">
                    <h3 className="board-replies-title">
                        {t('board.replies')} {replies.length > 0 && <span className="board-reply-count">{replies.length}</span>}
                    </h3>

                    {replies.length === 0 ? (
                        <p className="board-no-replies">{t('board.noReplies')}</p>
                    ) : (
                        <div className="board-reply-list">
                            {replies.map(reply => {
                                const replyUser = reply.user_info || reply.User;
                                const replyIsMine = reply.user_id === user?.user_id;
                                const isEditing = editingReplyId === reply.reply_id;

                                return (
                                    <div key={reply.reply_id} className="board-reply-item">
                                        <div className="board-reply-header">
                                            <span className="board-reply-author">
                                                {reply.is_admin && <span className="board-admin-badge">{t('board.admin')}</span>}
                                                {replyUser?.display_name || replyUser?.username || ''}
                                            </span>
                                            <span className="board-reply-date">{formatDate(reply.created_at)}</span>
                                        </div>
                                        {isEditing ? (
                                            <div className="board-reply-edit-form">
                                                <textarea
                                                    className="board-reply-textarea"
                                                    value={editingReplyContent}
                                                    onChange={e => setEditingReplyContent(e.target.value)}
                                                    rows={3}
                                                />
                                                <div className="board-reply-edit-actions">
                                                    <button className="board-btn board-btn-submit" onClick={() => handleReplyEdit(reply.reply_id)}>
                                                        {t('save')}
                                                    </button>
                                                    <button className="board-btn board-btn-cancel" onClick={() => { setEditingReplyId(null); setEditingReplyContent(''); }}>
                                                        {t('cancel')}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="board-reply-content">
                                                    {reply.content?.split('\n').map((line, i) => (
                                                        <React.Fragment key={i}>{line}<br /></React.Fragment>
                                                    ))}
                                                </div>
                                                {(replyIsMine || isAdminUser) && (
                                                    <div className="board-reply-actions">
                                                        {replyIsMine && (
                                                            <button className="board-reply-action-btn" onClick={() => { setEditingReplyId(reply.reply_id); setEditingReplyContent(reply.content); }}>
                                                                {t('board.edit')}
                                                            </button>
                                                        )}
                                                        <button className="board-reply-action-btn board-reply-delete-btn" onClick={() => handleReplyDelete(reply.reply_id)}>
                                                            {t('board.delete')}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="board-reply-form">
                        <textarea
                            className="board-reply-textarea"
                            placeholder={t('board.replyPlaceholder')}
                            value={replyContent}
                            onChange={e => setReplyContent(e.target.value)}
                            rows={3}
                        />
                        <button
                            className="board-btn board-btn-submit board-reply-submit-btn"
                            onClick={handleReplySubmit}
                            disabled={!replyContent.trim()}
                        >
                            {t('board.replySubmit')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 목록
    return (
        <div className="board-container">
            <div className="board-header">
                <h2>{t('board.suggestion')}</h2>
                <button className="board-btn board-btn-write" onClick={handleCreate}>
                    {t('board.write')}
                </button>
            </div>

            {loading ? (
                <div className="board-loading">{t('loading')}</div>
            ) : posts.length === 0 ? (
                <div className="board-empty">{t('board.empty')}</div>
            ) : (
                <>
                    <div className="board-list">
                        {posts.map(post => (
                            <div
                                key={post.post_id}
                                className={`board-list-item${post.is_pinned ? ' board-list-item-pinned' : ''}`}
                                onClick={() => fetchDetail(post.post_id)}
                            >
                                <div className="board-list-item-left">
                                    {post.is_pinned && (
                                        <span className="board-pinned-badge">{t('board.pinned')}</span>
                                    )}
                                    {post.visibility === 'private' && (
                                        <span className="board-private-badge">{t('board.private')}</span>
                                    )}
                                    <span className="board-list-item-title">{post.title}</span>
                                    {post.reply_count > 0 && (
                                        <span className="board-list-item-reply-count">[{post.reply_count}]</span>
                                    )}
                                </div>
                                <div className="board-list-item-right">
                                    <span className="board-list-item-author">
                                        {post.is_author_admin && <span className="board-admin-badge">{t('board.admin')}</span>}
                                        {getDisplayName(post)}
                                    </span>
                                    <span className="board-list-item-date">{formatDate(post.created_at)}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="board-pagination">
                            <button
                                disabled={page <= 1}
                                onClick={() => fetchPosts(page - 1)}
                            >&#8249;</button>
                            <span>{page} / {totalPages}</span>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => fetchPosts(page + 1)}
                            >&#8250;</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default Board;
